import {WebStorage} from '@/_application/core/storage/web-storage';
import {StoreView, StoreViewEnum} from '@/_application/modules/stores/model/store-view.model';
import {
    currentRegionAtom,
    currentStoreViewAtom,
    defaultStoreViewState,
} from '@/_application/modules/stores/store/store-view.store';
import {WEB_CART_KEY} from '@/lib/constants';
import {cartIdAtom} from '@/store/cart';
import {useAtom} from 'jotai';
import Cookies from 'js-cookie';
import React, {createContext, useContext, useState, PropsWithChildren, useMemo, useEffect, useCallback} from 'react';
import {Region} from '__generated__/__types__';
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import {defaultNorthSideRegion, defaultSouthSideRegion} from '@kidslabs/module-core';
import {findStoreViewByRegion} from './model/store-view';

export type StoreViewProviderState = {
    currentRegion?: Region;
    saveCurrentRegion: (region: Region) => void;
    currentStoreView?: StoreView;
    saveCurrentStoreView: (storeView: StoreView) => void;
    handleOpenStoreSwitcherModal: () => void;
};

const StoreViewContext = createContext<StoreViewProviderState>(defaultStoreViewState);

StoreViewContext.displayName = 'RegionsContext';

export const useStoreView = () => {
    const context = useContext(StoreViewContext);
    if (context === undefined) {
        throw new Error(`useStoreView must be used within a StoreViewProvider`);
    }
    return useMemo(() => context, [context]);
};

export const StoreViewProvider = ({children}: PropsWithChildren<{}>) => {
    const {openModal} = useModalAction();
    const [currentStoreView, setCurrentStoreView] = useAtom(currentStoreViewAtom);
    const [currentRegion, setCurrentRegion] = useAtom(currentRegionAtom);
    const [, setCartId] = useAtom(cartIdAtom);
    const [internalCurrentStoreView, setInternalCurrentStoreView] = useState(currentStoreView);
    const [internalCurrentRegion, setInternalCurrentRegion] = useState(currentRegion);

    const saveCurrentRegion = useCallback((region: Region) => {
        setCurrentRegion(region);
        setInternalCurrentRegion(region);
    }, []);

    const saveCurrentStoreView = (storeView: StoreView) => {
        setCurrentStoreView(storeView);
        setInternalCurrentStoreView(storeView);

        // Update store code in cookie
        Cookies.set('store', storeView.store_code, {expires: 365});
        if (currentStoreView?.store_code !== storeView.store_code) {
            WebStorage.removeItem(WEB_CART_KEY);
            WebStorage.removeItem('xCartTotal');
            WebStorage.removeItem('xCartItems');
            setCartId('');
        }
    };

    const handleOpenStoreSwitcherModal = useCallback(() => {
        openModal('STORE_SWITCHER');
    }, []);

    const handleInitExistedStoreFromCookie = useCallback(() => {
        const existedStoreCode = Cookies.get('store');
        if (!existedStoreCode || existedStoreCode === currentStoreView?.store_code) {
            return;
        }

        const initRegion = existedStoreCode === StoreViewEnum.HCM ? defaultSouthSideRegion : defaultNorthSideRegion;
        findStoreViewByRegion(initRegion).then(storeView => {
            if (!storeView || currentStoreView?.store_code === storeView.store_code) {
                return;
            }
            setCurrentStoreView(storeView);
            setInternalCurrentStoreView(storeView);
            saveCurrentRegion(initRegion);
        });
    }, [currentStoreView?.store_code, saveCurrentRegion]);

    useEffect(() => {
        const existedStoreCode = Cookies.get('store');
        if (!existedStoreCode) {
            handleOpenStoreSwitcherModal();
        } else {
            handleInitExistedStoreFromCookie();
        }
    }, [handleOpenStoreSwitcherModal, handleInitExistedStoreFromCookie]);

    const value = useMemo(
        () => ({
            currentRegion,
            saveCurrentRegion,
            currentStoreView,
            saveCurrentStoreView,
            internalCurrentStoreView,
            internalCurrentRegion,
            handleOpenStoreSwitcherModal,
        }),
        [internalCurrentStoreView?.store_code, internalCurrentRegion?.code],
    );

    return <StoreViewContext.Provider value={value}>{children}</StoreViewContext.Provider>;
};
